<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import IndividualAccountLayout from '@/modules/investing/components/individuals/individual-account-layout.vue'
import IndividualContactForm from '@/modules/investing/components/individuals/individual-contact-form.vue'
import IndividualLayout from '@/modules/investing/components/individuals/individual-layout.vue'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'
import { useContactStore } from '@/modules/investing/stores/contact-store'
import { VButton, VIcon, VTable } from '@/modules/shared/components'
import { useModal } from '@/modules/shared/composables/use-modal'

const contactStore = useContactStore()
const individualStore = useIndividualStore()

///////////////////////////////////////////////////////////////////////////////
// Calls
///////////////////////////////////////////////////////////////////////////////

const contacts = computed(() => individualStore.listContacts)

///////////////////////////////////////////////////////////////////////////////
// Actions
///////////////////////////////////////////////////////////////////////////////

const { individual_id, slug } = useRoute().params as { individual_id: string; slug: string }

const fetch = async () => {
  await individualStore.fetchContacts(individual_id, { slug })
}

const deleteContact = async (contact_id: number) => {
  if (!window.confirm('Are you sure?')) return
  await contactStore.removeContact(contact_id)
  await fetch()
}

const showForm = () => {
  contactFormModal.open()
}

const addContact = async (payload) => {
  await contactStore.addContact(payload)
  await fetch()
  contactFormModal.close()
}

///////////////////////////////////////////////////////////////////////////////
// Authorization
///////////////////////////////////////////////////////////////////////////////

const authStore = useAuthStore()
const isAdmin = computed(
  () => individual_id === authStore.current_user.investor_id?.toString() || authStore.is_site_or_group_admin,
)

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()
const contactFormModal = useModal()

onMounted(async () => {
  showSkeleton()
  await individualStore.fetchIndividual(individual_id, { slug })
  await fetch()
  hideSkeleton()
})
</script>

<template>
  <IndividualLayout selectedTab="account">
    <IndividualAccountLayout selectedTab="contacts">
      <div class="mb-6 flex flex-row-reverse items-center gap-3">
        <VButton :click="showForm" size="md" variant="v-blue">
          <div class="mr-1 flex items-center space-x-2">
            <div><VIcon name="Plus" /></div>
            <div>Add Contact</div>
          </div>
        </VButton>
      </div>
      <VTable
        :columns="[
          {
            key: 'name',
            name: 'Name',
            type: 'string',
            fixed: true,
            is_visible: true,
          },
          {
            key: 'email',
            name: 'Email',
            type: 'string',
            is_visible: true,
          },
          {
            key: 'date',
            name: 'Date Added',
            sorted: true,
            type: 'date',
            is_visible: true,
          },
          {
            key: 'actions',
            name: '',
            type: 'actions',
            is_visible: true,
          },
        ]"
        :items="contacts"
        :skeleton="skeleton"
        name="individual-contacts"
        :slots="['actions']"
        no_data_message="Please add anyone here you would like copied on correspondence such as emails and invoices."
      >
        <template #actions="{ item }">
          <div class="flex space-x-1">
            <VButton :click="() => deleteContact(item.id)" size="xs"> Remove </VButton>
          </div>
        </template>
      </VTable>
    </IndividualAccountLayout>
    <IndividualContactForm :modalStore="contactFormModal" :onSubmit="addContact" />
  </IndividualLayout>
</template>
